import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { createInvitation, sendInvitation } from '../../../services/meetingService';
import { toast } from 'react-toastify';

const InviteParticipantModal = ({ isOpen, onClose, meetingId, inviterName }) => {
  const [inviteeEmail, setInviteeEmail] = useState('');
  const [inviteePhone, setInviteePhone] = useState('');
  const [inviteeName, setInviteeName] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSMS, setSendSMS] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failureState, setFailureState] = useState(null); // { type: 'sms' | 'email', invitationId, error }

  const handleSendInvitation = async () => {
    if (!inviteeName?.trim()) {
      toast?.error('Please enter participant name');
      return;
    }

    if (!inviteeEmail?.trim() && !inviteePhone?.trim()) {
      toast?.error('Please enter email or phone number');
      return;
    }

    try {
      setLoading(true);
      setFailureState(null);

      // Create invitation
      const invitation = await createInvitation({
        meetingId,
        inviteeEmail: inviteeEmail?.trim() || null,
        inviteePhone: inviteePhone?.trim() || null,
        inviteeName: inviteeName?.trim()
      });

      if (!invitation) {
        toast?.error('Failed to create invitation');
        return;
      }

      // Send invitation via email/SMS
      const result = await sendInvitation(invitation?.id, sendEmail && !!inviteeEmail, sendSMS && !!inviteePhone);

      if (result?.emailSent || result?.smsSent) {
        const sentVia = [];
        if (result?.emailSent) sentVia?.push('email');
        if (result?.smsSent) sentVia?.push('SMS');
        toast?.success(`Invitation sent to ${inviteeName} via ${sentVia?.join(' and ')}!`);
        onClose();
        setInviteeEmail('');
        setInviteePhone('');
        setInviteeName('');
        setFailureState(null);
      } else {
        // Determine failure type and show retry/fallback options
        if (result?.smsError && sendSMS) {
          setFailureState({
            type: 'sms',
            invitationId: invitation?.id,
            error: result?.smsError,
            hasEmailFallback: !!inviteeEmail
          });
        } else if (result?.emailError && sendEmail) {
          setFailureState({
            type: 'email',
            invitationId: invitation?.id,
            error: result?.emailError,
            hasSmsFallback: !!inviteePhone
          });
        }

        // Show specific error messages
        let errorMsg = 'Invitation created but not sent. ';
        if (result?.emailError) {
          errorMsg += `Email error: ${result?.emailError}. `;
        }
        if (result?.smsError) {
          errorMsg += `SMS error: ${result?.smsError}. `;
        }
        if (!result?.emailError && !result?.smsError) {
          errorMsg += 'Check notification settings or verify credentials in Supabase.';
        }
        toast?.warning(errorMsg);
      }
    } catch (error) {
      console.error('Send invitation error:', error);
      toast?.error(error?.message || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleRetrySMS = async () => {
    if (!failureState?.invitationId) return;

    try {
      setLoading(true);
      const result = await sendInvitation(failureState?.invitationId, false, true);

      if (result?.smsSent) {
        toast?.success(`SMS invitation sent to ${inviteeName}!`);
        onClose();
        setInviteeEmail('');
        setInviteePhone('');
        setInviteeName('');
        setFailureState(null);
      } else {
        toast?.error(`SMS retry failed: ${result?.smsError || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Retry SMS error:', error);
      toast?.error(error?.message || 'Failed to retry SMS');
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchToEmail = async () => {
    if (!failureState?.invitationId) return;

    try {
      setLoading(true);
      const result = await sendInvitation(failureState?.invitationId, true, false);

      if (result?.emailSent) {
        toast?.success(`Email invitation sent to ${inviteeName}!`);
        onClose();
        setInviteeEmail('');
        setInviteePhone('');
        setInviteeName('');
        setFailureState(null);
      } else {
        toast?.error(`Email send failed: ${result?.emailError || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Switch to email error:', error);
      toast?.error(error?.message || 'Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchToSMS = async () => {
    if (!failureState?.invitationId) return;

    try {
      setLoading(true);
      const result = await sendInvitation(failureState?.invitationId, false, true);

      if (result?.smsSent) {
        toast?.success(`SMS invitation sent to ${inviteeName}!`);
        onClose();
        setInviteeEmail('');
        setInviteePhone('');
        setInviteeName('');
        setFailureState(null);
      } else {
        toast?.error(`SMS send failed: ${result?.smsError || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Switch to SMS error:', error);
      toast?.error(error?.message || 'Failed to send SMS');
    } finally {
      setLoading(false);
    }
  };

  const handleRetryEmail = async () => {
    if (!failureState?.invitationId) return;

    try {
      setLoading(true);
      const result = await sendInvitation(failureState?.invitationId, true, false);

      if (result?.emailSent) {
        toast?.success(`Email invitation sent to ${inviteeName}!`);
        onClose();
        setInviteeEmail('');
        setInviteePhone('');
        setInviteeName('');
        setFailureState(null);
      } else {
        toast?.error(`Email retry failed: ${result?.emailError || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Retry email error:', error);
      toast?.error(error?.message || 'Failed to retry email');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-xl font-semibold text-foreground">Invite Participant</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Participant Name *
            </label>
            <Input
              type="text"
              value={inviteeName}
              onChange={(e) => setInviteeName(e?.target?.value)}
              placeholder="Enter name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Email Address
            </label>
            <Input
              type="email"
              value={inviteeEmail}
              onChange={(e) => setInviteeEmail(e?.target?.value)}
              placeholder="participant@example.com"
            />
            <div className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                id="sendEmail"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e?.target?.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
              />
              <label htmlFor="sendEmail" className="text-sm text-muted-foreground">
                Send email invitation
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Phone Number
            </label>
            <Input
              type="tel"
              value={inviteePhone}
              onChange={(e) => setInviteePhone(e?.target?.value)}
              placeholder="+1 (555) 123-4567"
            />
            <div className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                id="sendSMS"
                checked={sendSMS}
                onChange={(e) => setSendSMS(e?.target?.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
              />
              <label htmlFor="sendSMS" className="text-sm text-muted-foreground">
                Send SMS invitation
              </label>
            </div>
          </div>

          {failureState && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-2">
                <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-destructive mb-1">
                    {failureState?.type === 'sms' ? 'SMS Delivery Failed' : 'Email Delivery Failed'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {failureState?.error}
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col gap-2">
                {failureState?.type === 'sms' && (
                  <>
                    <Button
                      onClick={handleRetrySMS}
                      variant="outline"
                      size="sm"
                      disabled={loading}
                      className="w-full border-primary text-primary hover:bg-primary/10"
                    >
                      <Icon name="RefreshCw" size={14} className="mr-2" />
                      {loading ? 'Retrying...' : 'Retry SMS'}
                    </Button>
                    {failureState?.hasEmailFallback && (
                      <Button
                        onClick={handleSwitchToEmail}
                        variant="outline"
                        size="sm"
                        disabled={loading}
                        className="w-full"
                      >
                        <Icon name="Mail" size={14} className="mr-2" />
                        {loading ? 'Sending...' : 'Send via Email Instead'}
                      </Button>
                    )}
                  </>
                )}
                {failureState?.type === 'email' && (
                  <>
                    <Button
                      onClick={handleRetryEmail}
                      variant="outline"
                      size="sm"
                      disabled={loading}
                      className="w-full border-primary text-primary hover:bg-primary/10"
                    >
                      <Icon name="RefreshCw" size={14} className="mr-2" />
                      {loading ? 'Retrying...' : 'Retry Email'}
                    </Button>
                    {failureState?.hasSmsFallback && (
                      <Button
                        onClick={handleSwitchToSMS}
                        variant="outline"
                        size="sm"
                        disabled={loading}
                        className="w-full"
                      >
                        <Icon name="MessageSquare" size={14} className="mr-2" />
                        {loading ? 'Sending...' : 'Send via SMS Instead'}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          <div className="bg-primary/10 border border-primary/20 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} className="text-primary flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">
                Invitation message: "{inviterName} is inviting you to meetup with Mosey"
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendInvitation}
            className="flex-1"
            disabled={loading || !!failureState}
          >
            {loading ? 'Sending...' : 'Send Invitation'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InviteParticipantModal;